import { useReadiedQuery } from "@itandi/tools/next";
import { parsePositiveInteger } from "@itandi/tools/vanilla";
import { useEffect, useState } from "react";
import { DefaultV2 } from "~/components/common/layouts/DefaultV2";
import Loading from "~/components/common/parts/loading/Loading";
import { ProcessingState } from "~/components/specifics/deals/[id]/estimations/ocr_requests/[requestId]/ProcessingState";
import { SucceededState } from "~/components/specifics/deals/[id]/estimations/ocr_requests/[requestId]/SucceededState";
import { UnavailableState } from "~/components/specifics/deals/[id]/estimations/ocr_requests/[requestId]/UnavailableState";
import { useDealEstimationOcrRequest } from "~/hooks/deals/useDealEstimationOcrRequest";
import type { DealEstimationOcrRequest } from "~/models/DealEstimationOcrRequest";
import { PATH as ESTIMATIONS_EDIT_PATH } from "~/pages/deals/[id]/estimations/edit";

// 3分経ったら強制的に中断する
const PROCESSING_TIMEOUT_MS = 3 * 60 * 1_000;

function renderContent(
	backToEditPath: string,
	ocrRequest: DealEstimationOcrRequest,
	isProcessingTimedOut: boolean
) {
	if (isProcessingTimedOut) {
		return (
			<UnavailableState
				backToEditPath={backToEditPath}
				message="見積書の読み取りに時間がかかっているため、処理を中断しました。時間をおいて再度お試しください。"
				variant="error"
			/>
		);
	}

	if (ocrRequest.status === "failed") {
		return (
			<UnavailableState
				backToEditPath={backToEditPath}
				message={
					ocrRequest.message ??
					"見積書の読み取りに失敗しました。時間をおいて再度お試しください。"
				}
				variant="error"
			/>
		);
	}

	if (ocrRequest.status === "processing") {
		return <ProcessingState backToEditPath={backToEditPath} />;
	}

	if (ocrRequest.reflectedAt !== null) {
		return (
			<UnavailableState
				backToEditPath={backToEditPath}
				message="この読み取り結果はすでに見積へ反映済みです。"
				variant="info"
			/>
		);
	}

	return (
		<SucceededState backToEditPath={backToEditPath} ocrRequest={ocrRequest} />
	);
}

export function Page() {
	const dealId = useReadiedQuery("id", (q) => {
		const parsed = parsePositiveInteger(q);

		if (parsed === null) {
			throw new Error("Invalid Deal ID");
		}

		return parsed;
	});
	const requestId = useReadiedQuery("requestId", (q) => {
		const parsed = parsePositiveInteger(q);

		if (parsed === null) {
			throw new Error("Invalid OCR Request ID");
		}

		return parsed;
	});

	const { data: ocrRequest } = useDealEstimationOcrRequest(
		dealId,
		requestId,
		true
	);
	const [isProcessingTimedOut, setIsProcessingTimedOut] = useState(false);
	const isProcessing = ocrRequest?.status === "processing";

	useEffect(() => {
		if (!isProcessing) return undefined;
		const timer = setTimeout(() => {
			setIsProcessingTimedOut(true);
		}, PROCESSING_TIMEOUT_MS);
		return (): void => {
			clearTimeout(timer);
		};
	}, [isProcessing]);

	if (ocrRequest === null) {
		return <Loading isLoading />;
	}

	const backToEditPath = ESTIMATIONS_EDIT_PATH(dealId);

	return (
		<DefaultV2>
			{renderContent(backToEditPath, ocrRequest, isProcessingTimedOut)}
		</DefaultV2>
	);
}
